import { useEffect, useState } from 'react'; // Add useState
import { Button } from '../ui/Button';
import { PlusCircle, MessageSquare, Loader2, HelpCircle } from 'lucide-react';
import AddChannelModal from './AddChannelModal';
import ChannelCard from './ChannelCard'; // Import ChannelCard
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchQrCodeForNewChannel, getChannels } from '../../services/channelService';
import apiClient from '../../lib/apiClient';
import { subscribeToEvent, unsubscribeFromEvent, getSocket } from '../../lib/websocketService'; // Added getSocket
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

const NEW_CHANNEL_EVENT_NAME = 'authenticated';
const LOGOUT_EVENT_NAME = 'wa-logout';
const RECONNECTING_EVENT_NAME = 'channel-reconnecting';
export default function ChannelListPage() {
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [base64QrString, setBase64QrString] = useState<string | null>(null);
  const [qrChannelId, setQrChannelId] = useState<number | null>(null);
  const queryClient = useQueryClient();

  const startTour = () => {
    const steps: any[] = [
      {
        element: '#channels-page-title',
        popover: {
          title: t('yourChannels'),
          description: t('tourChannelsPageDesc') || 'Esta página muestra todos tus canales de WhatsApp configurados. Aquí puedes gestionar tus conexiones y agregar nuevos canales.',
          side: "bottom",
          align: 'start'
        }
      },
      {
        element: '#add-channel-btn',
        popover: {
          title: t('addChannel'),
          description: t('tourAddChannelBtnDesc') || 'Haz click en este botón para agregar un nuevo canal de WhatsApp. Se generará un código QR que deberás escanear con tu teléfono.',
          side: "bottom",
          align: 'end'
        }
      }
    ];

    // Solo agregamos el paso de la grilla si hay canales
    if (channels.length > 0) {
      steps.push({
        element: '#channels-grid',
        popover: {
          title: t('channelsList'),
          description: t('tourChannelsListDesc') || 'Aquí se muestran todos tus canales activos. Cada tarjeta muestra el nombre del canal, número de teléfono y estado de autenticación.',
          side: "top",
          align: 'start'
        }
      });
    }

    // Agregamos un paso final que pregunta si quiere ver el proceso completo
    steps.push({
      popover: {
        title: t('seeAddChannelProcess'),
        description: t('tourSeeProcessDesc') || '¿Quieres ver el proceso completo de cómo agregar un canal? Haz click en "Siguiente" para abrir el modal y ver los pasos.',
        side: "top",
        align: 'center'
      }
    });

    const driverObj = driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      steps,
      onNextClick: (element, step, options) => {
        // Si es el último paso, iniciamos el tour del modal
        if (options.state.activeIndex === steps.length - 1) {
          driverObj.destroy();
          startAddChannelTour();
        } else {
          driverObj.moveNext();
        }
      }
    });

    driverObj.drive();
  };

  const { 
    data: channels = [], // Default to empty array
    isLoading: isLoadingChannels, 
    isError: isErrorChannels,
    error: channelsError // For displaying specific error message
  } = useQuery({ // Specify Error type for error object
    queryKey: ['channels'],
    queryFn: getChannels,
  });

  useEffect(() => {
    const handleAuthenticated = (payload: any) => {
      console.log('WebSocket: Evento authenticated recibido', payload);
      if (payload.nameChannel) {
        toast.success(t('channelAdded', { channelName: payload.nameChannel }));
      } else if (payload.channelId) {
        toast.success(t('channelAuthenticated'));
      }
      setIsModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    };

    const handleLogout = (payload: any) => {
      console.log('WebSocket: Evento wa-logout recibido', payload);
      toast.error(t('channelDisconnected'));
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    };

    // Solo dispara un refetch — no muestra toast, puede llegar varias veces
    // mientras el backend reintenta conectar un canal en segundo plano.
    const handleReconnecting = (payload: any) => {
      console.log('WebSocket: Evento channel-reconnecting recibido', payload);
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    };

    const socket = getSocket();
    let connectListener: (() => void) | undefined;

    if (socket && socket.connected) {
      subscribeToEvent<any>(NEW_CHANNEL_EVENT_NAME, handleAuthenticated);
      subscribeToEvent<any>(LOGOUT_EVENT_NAME, handleLogout);
      subscribeToEvent<any>(RECONNECTING_EVENT_NAME, handleReconnecting);
    } else {
      connectListener = () => {
        console.log("WebSocket connected, subscribing to events in ChannelListPage.");
        subscribeToEvent<any>(NEW_CHANNEL_EVENT_NAME, handleAuthenticated);
        subscribeToEvent<any>(LOGOUT_EVENT_NAME, handleLogout);
        subscribeToEvent<any>(RECONNECTING_EVENT_NAME, handleReconnecting);
        socket?.off('connect', connectListener);
      };
      socket?.on('connect', connectListener);
    }
    return () => {
      unsubscribeFromEvent(NEW_CHANNEL_EVENT_NAME, handleAuthenticated);
      unsubscribeFromEvent(LOGOUT_EVENT_NAME, handleLogout);
      unsubscribeFromEvent(RECONNECTING_EVENT_NAME, handleReconnecting);
      if (connectListener) {
        socket?.off('connect', connectListener);
      }
    };
  }, [queryClient, t]);

  const qrCodeMutation = useMutation<string, Error, { channelId?: number }>({
    mutationFn: async ({ channelId }) => {
      if (channelId) {
        console.log("Fetching QR code for existing channel ID:", channelId);
        // Llama al endpoint para ese canal específico
        const res = await apiClient.get(`/whatsapp/qr/${channelId}`);
        // Si la respuesta es un objeto tipo AxiosResponse, extrae el data
        return (res && typeof res === 'object' && 'data' in res) ? res.data : res;
      } else {
        console.log("Fetching QR code for new channel.");
        // Lógica original para nuevo canal
        return fetchQrCodeForNewChannel();
      }
    },
    onSuccess: (data) => {
      setBase64QrString(data);
      setIsModalOpen(true);
    },
    onError: (error) => {
      setBase64QrString(null);
      setIsModalOpen(true);
    },
  });

  const handleAddChannelClick = () => {
    setBase64QrString(null);
    setQrChannelId(null);
    setIsModalOpen(true);
    qrCodeMutation.mutate({});
  };

  const startAddChannelTour = () => {
    // Primero abrimos el modal
    setBase64QrString(null);
    setQrChannelId(null);
    setIsModalOpen(true);
    qrCodeMutation.mutate({});
    
    // Esperamos un momento para que el modal se renderice
    setTimeout(() => {
      const driverObj = driver({
        showProgress: true,
        showButtons: ['next', 'previous', 'close'],
        steps: [
          {
            element: '#qr-code-container',
            popover: {
              title: t('qrCodeGeneration'),
              description: t('tourQRContainerDesc') || 'Aquí se genera y muestra el código QR. Este código es único para tu nuevo canal de WhatsApp.',
              side: "bottom",
              align: 'center'
            }
          },
          {
            element: '#qr-code-image',
            popover: {
              title: t('scanQRCode'),
              description: t('tourQRImageDesc') || 'Escanea este código QR con la aplicación de WhatsApp en tu teléfono. Ve a WhatsApp > Ajustes > Dispositivos vinculados > Vincular dispositivo.',
              side: "bottom",
              align: 'center'
            }
          },
          {
            element: '#qr-instructions',
            popover: {
              title: t('instructions'),
              description: t('tourQRInstructionsDesc') || 'Sigue estas instrucciones para vincular correctamente tu canal. Una vez escaneado, el canal se agregará automáticamente.',
              side: "top",
              align: 'center'
            }
          }
        ],
        onDestroyStarted: () => {
          setIsModalOpen(false);
          driverObj.destroy();
        }
      });

      driverObj.drive();
    }, 500);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setQrChannelId(null);
  };
  const handleRetryFetchQr = () => {
    qrCodeMutation.mutate({ channelId: qrChannelId ?? undefined });
  };
  const qrErrorForModal = qrCodeMutation.error ? (qrCodeMutation.error as Error).message : null;

  // Manejar evento global para re-escanear QR de canal
  useEffect(() => {
    const handleOpenQrModal = (e: any) => {
      const channelId = e.detail?.channelId;
      if (channelId) {
        setBase64QrString(null);
        setQrChannelId(channelId);
        setIsModalOpen(true);
        qrCodeMutation.mutate({ channelId });
      }
    };
    window.addEventListener('openQrModal', handleOpenQrModal);
    return () => window.removeEventListener('openQrModal', handleOpenQrModal);
  }, [qrCodeMutation]);

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 id="channels-page-title" className="text-2xl font-semibold text-gray-800">{t("yourChannels")}</h1>
          <button
            onClick={startTour}
            className="rounded-full p-2 text-gray-600 transition hover:bg-gray-100 hover:text-indigo-600"
            title={t('startTour') || 'Iniciar tour'}
          >
            <HelpCircle className="h-6 w-6" />
          </button>
        </div>
        <Button id="add-channel-btn" onClick={handleAddChannelClick} className="bg-green-500 hover:bg-green-600 text-white flex items-center" disabled={qrCodeMutation.isPending}>
          {qrCodeMutation.isPending ? ( <Loader2 className="mr-2 h-5 w-5 animate-spin" /> ) : ( <PlusCircle className="mr-2 h-5 w-5" /> )}
          {t('addChannel')}
        </Button>
      </div>

      {isLoadingChannels && channels.length === 0 && ( // Show loader only when no channels are displayed yet
        <div className="flex justify-center items-center py-10">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
          <span className="ml-3 text-gray-600">{t('loadingChannels')}</span>
        </div>
      )}

      {!isLoadingChannels && isErrorChannels && (
        <div className="text-center text-red-600 py-10 border-2 border-dashed border-red-300 bg-red-50 rounded-lg p-6">
          <p className="font-semibold">{t('failedToLoadChannels')}</p>
          <p className="text-sm">{channelsError?.message || t('unexpectedError')}</p>
          <Button onClick={() => queryClient.refetchQueries({ queryKey: ['channels']})} variant="outline" size="sm" className="mt-4 border-red-300 text-red-700 hover:bg-red-100">
            {t('retry')}
          </Button> 
        </div>
      )}

      {!isLoadingChannels && !isErrorChannels && channels.length === 0 && (
        <div className="text-center text-gray-500 py-10 border-2 border-dashed border-gray-300 rounded-lg p-6">
          <MessageSquare size={48} className="mx-auto mb-4 opacity-70" />
          <p className="text-lg">{t('noChannelsYet')}</p>
          <p className="text-sm">{t('noChannelsDescription')}</p>
        </div>
      )}

      {!isErrorChannels && channels.length > 0 && (
        <div id="channels-grid" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {channels.map(channel => (
            <ChannelCard key={channel.id} channel={channel} />
          ))}
        </div>
      )}

      <AddChannelModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        base64QrString={base64QrString}
        isLoadingQr={qrCodeMutation.isPending}
        errorQr={qrErrorForModal}
        onRetryFetchQr={handleRetryFetchQr}
      />
    </div>
  );
}

